import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Admin.css';
import API_BASE from '../config';

function AuditLogs() {
  const navigate = useNavigate();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    severity: '',
    resource: '',
    limit: 100
  });
  const [expandedLog, setExpandedLog] = useState(null);
  const [collapsedDates, setCollapsedDates] = useState(new Set());

  const ACTIONS = ['LOGIN', 'LOGOUT', 'PERMISSION_CHANGED', 'ACCESS_GRANTED', 'ACCESS_REVOKED', 'USER_DELETED', 'ROLE_CREATED'];
  const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
  const RESOURCES = ['user', 'role', 'permission', 'access_control', 'asset', 'audit'];

  useEffect(() => {
    fetchAuditLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const query = new URLSearchParams();
      if (filters.action) query.append('action', filters.action);
      if (filters.severity) query.append('severity', filters.severity);
      if (filters.resource) query.append('resource', filters.resource);
      query.append('limit', filters.limit);

      const response = await fetch(
        `${API_BASE}/admin/audit-logs?${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.auditLogs || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'CRITICAL': return '#d32f2f';
      case 'WARNING': return '#f57c00';
      default: return '#388e3c';
    }
  };

  const getStatusColor = (status) => {
    return status === 'SUCCESS' ? '#4caf50' : '#f44336';
  };

  if (loading) return <div className="admin-container"><p>Loading audit logs...</p></div>;

  // Group logs by date
  const groupedByDate = auditLogs.reduce((groups, log) => {
    const dateKey = new Date(log.createdAt).toLocaleDateString('en-GB'); // DD/MM/YYYY
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(log);
    return groups;
  }, {});
  const dateKeys = Object.keys(groupedByDate); // already sorted by most recent first from API

  const toggleDate = (dateKey) => {
    setCollapsedDates(prev => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  return (
    <div className="admin-container">
      <div className="page-header">
        <button onClick={() => navigate('/admin')} className="btn-back-nav" style={{ marginBottom: 12 }}>← Back</button>
        <h1>Audit Logs</h1>
        <p>Track all administrative actions for compliance and accountability</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <select 
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          className="filter-select"
        >
          <option value="">All Actions</option>
          {ACTIONS.map(action => (
            <option key={action} value={action}>{action}</option>
          ))}
        </select>

        <select 
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          className="filter-select"
        >
          <option value="">All Severities</option>
          {SEVERITIES.map(sev => (
            <option key={sev} value={sev}>{sev}</option>
          ))}
        </select>

        <select 
          value={filters.resource}
          onChange={(e) => setFilters({ ...filters, resource: e.target.value })}
          className="filter-select"
        >
          <option value="">All Resources</option>
          {RESOURCES.map(res => (
            <option key={res} value={res}>{res}</option>
          ))}
        </select>

        <select 
          value={filters.limit}
          onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
          className="filter-select"
        >
          <option value={50}>Last 50</option>
          <option value={100}>Last 100</option>
          <option value={500}>Last 500</option>
          <option value={1000}>Last 1000</option>
        </select>
      </div>

      {/* Audit Logs grouped by date */}
      <div className="section">
        <h2>Activity Log ({auditLogs.length} events)</h2>
        
        <div className="audit-logs-container">
          {auditLogs.length === 0 ? (
            <p>No audit logs found</p>
          ) : (
            dateKeys.map(dateKey => (
              <div key={dateKey} style={{ marginBottom: 6 }}>
                {/* Date directory header */}
                <div
                  onClick={() => toggleDate(dateKey)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', background: '#1a237e', color: '#fff',
                    borderRadius: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 4
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 700 }}>
                    {collapsedDates.has(dateKey) ? '▶' : '▼'}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.3 }}>
                    📁 {dateKey}
                  </span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                    {groupedByDate[dateKey].length} event{groupedByDate[dateKey].length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Logs within this date */}
                {!collapsedDates.has(dateKey) && groupedByDate[dateKey].map(log => (
              <div 
                key={log.id} 
                className="audit-log-entry"
                style={{ borderLeft: `4px solid ${getSeverityColor(log.severity)}` }}
              >
                <div className="log-header">
                  <div className="log-info">
                    <span className="log-timestamp">
                      {new Date(log.createdAt).toLocaleString()}
                    </span>
                    <span className="log-action" style={{ color: '#1976d2' }}>
                      {log.action}
                    </span>
                    <span className="log-resource">
                      [{log.resource}]
                    </span>
                    <span 
                      className="log-severity"
                      style={{ 
                        backgroundColor: getSeverityColor(log.severity),
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {log.severity}
                    </span>
                    <span 
                      className="log-status"
                      style={{ 
                        backgroundColor: getStatusColor(log.status),
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {log.status}
                    </span>
                  </div>
                  <button 
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                    className="btn-expand"
                  >
                    {expandedLog === log.id ? '▼' : '▶'}
                  </button>
                </div>

                {log.user && (
                  <div className="log-user">
                    <strong>User:</strong> {log.user.firstName} {log.user.lastName} ({log.user.email})
                    <span className="user-type">{log.user.iso27001UserType}</span>
                  </div>
                )}

                {expandedLog === log.id && (
                  <div className="log-details">
                    {log.ipAddress && <p><strong>IP Address:</strong> {log.ipAddress}</p>}
                    {log.userAgent && <p><strong>User Agent:</strong> {log.userAgent}</p>}
                    {log.resourceId && <p><strong>Resource ID:</strong> {log.resourceId}</p>}
                    {log.errorMessage && <p><strong>Error:</strong> {log.errorMessage}</p>}
                    {log.changeDetails && (
                      <div>
                        <strong>Changes:</strong>
                        <pre>{JSON.stringify(log.changeDetails, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="section">
        <h2>Summary</h2>
        <div className="summary-grid">
          <div className="summary-card">
            <h4>Total Events</h4>
            <p className="big-number">{auditLogs.length}</p>
          </div>
          <div className="summary-card">
            <h4>Critical Events</h4>
            <p className="big-number" style={{ color: '#d32f2f' }}>
              {auditLogs.filter(log => log.severity === 'CRITICAL').length}
            </p>
          </div>
          <div className="summary-card">
            <h4>Successful Actions</h4>
            <p className="big-number" style={{ color: '#4caf50' }}>
              {auditLogs.filter(log => log.status === 'SUCCESS').length}
            </p>
          </div>
          <div className="summary-card">
            <h4>Failed Actions</h4>
            <p className="big-number" style={{ color: '#f44336' }}>
              {auditLogs.filter(log => log.status === 'FAILURE').length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuditLogs;
